import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Container, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import apiCalls from '../PowerShadesAPIFunctions';
import type { AwningOrdersReportRow } from '../Quote/awningTypes';
import Money from '../Parts/Money';
import Button from '../Parts/Button';
import InternalLink from '../Parts/InternalLink';
import { getQuoteLink } from '../psUtil';

type AwningOrdersReportProps = {
	setTitle: (title: string) => void;
	loaded: () => void;
};

const ORDER_STATUS_TITLES: Record<string, string> = {
	active_quote: 'Active Quote',
	archived: 'Archived Quote',
	order_pending_approval: 'Pending Approval',
	under_review: 'Under Review',
	awaiting_sales_approval: 'Awaiting Sales Approval',
	awaiting_po: 'Awaiting PO',
	approved_order: 'Ready For Manufacturing',
	mfg_accepted_order: 'In Production',
	mfg_provisioned_order: 'In Production',
	mfg_shipped_order: 'Order Shipped',
	submitted_quote: 'Submitted and Processing',
};

const formatOrderStatus = (status: string | null) => {
	if (!status) return '';
	return ORDER_STATUS_TITLES[status] ?? status;
};

const COLUMN_COUNT = 8;

/**
 * Awning Orders report - lists every non-archived awning quote with its entity names and
 * pricing snapshots. Awnings are built by KE rather than our MES, so this is the admin view
 * for keeping tabs on them across dealers.
 */
const AwningOrdersReport: FC<AwningOrdersReportProps> = ({ setTitle, loaded }) => {
	const [orders, setOrders] = useState<AwningOrdersReportRow[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('');

	const loadReport = useCallback(async () => {
		setIsLoading(true);

		try {
			const resp = await apiCalls.getAwningOrdersReport();
			const data = resp.data ?? null;

			// The backend reports failures as HTTP 200 with an `error` key, so `error`
			// gets checked before anything else.
			if (data?.error) {
				setError(data.error);
				setOrders([]);
			} else if (!data || !Array.isArray(data.orders)) {
				setError(resp.status === 403
					? 'You do not have permission to view this report.'
					: 'Unable to load the awning orders report.');
				setOrders([]);
			} else {
				setError('');
				setOrders(data.orders);
			}
		} catch (err) {
			console.error(err);
			setError('Unable to load the awning orders report.');
			setOrders([]);
		}

		setIsLoading(false);
	}, []);

	useEffect(() => {
		setTitle('Awning Orders');
		loaded();
		loadReport();
	}, [loadReport]);

	const statusOptions = useMemo(() => {
		const statuses = new Set<string>();
		orders.forEach((o) => {
			if (o.order_status) statuses.add(o.order_status);
		});
		return Array.from(statuses).sort((a, b) => formatOrderStatus(a).localeCompare(formatOrderStatus(b)));
	}, [orders]);

	const filteredOrders = useMemo(() => {
		const term = search.trim().toLowerCase();

		return orders.filter((o) => {
			if (statusFilter !== '' && o.order_status !== statusFilter) return false;
			if (term === '') return true;

			const haystack = [
				`${o.id}`,
				`PS${o.id}`,
				o.dealer_name ?? '',
				o.distributor_name ?? '',
				o.territory_name ?? '',
				formatOrderStatus(o.order_status),
			].join(' ').toLowerCase();

			return haystack.includes(term);
		});
	}, [orders, search, statusFilter]);

	const totals = useMemo(() => filteredOrders.reduce(
		(acc, o) => ({
			cost: acc.cost + (o.cost_snapshot ?? 0),
			dist: acc.dist + (o.dist_price_snapshot ?? 0),
			dealer: acc.dealer + (o.dealer_price_snapshot ?? 0),
		}),
		{ cost: 0, dist: 0, dealer: 0 }
	), [filteredOrders]);

	const renderMoney = (value: number | null) => (value === null || value === undefined
		? <span className="text-muted">Not priced</span>
		: <Money value={value} />);

	const rows = filteredOrders.map((o) => (
		<tr key={o.id}>
			<td>
				<InternalLink content={getQuoteLink(o.id)}>
					<span className="green-text">{`PS${o.id}`}</span>
				</InternalLink>
			</td>
			<td>{formatOrderStatus(o.order_status)}</td>
			<td>{o.dealer_name ?? ''}</td>
			<td>{o.distributor_name ?? ''}</td>
			<td>{o.territory_name ?? ''}</td>
			<td className="text-end">{renderMoney(o.cost_snapshot)}</td>
			<td className="text-end">{renderMoney(o.dist_price_snapshot)}</td>
			<td className="text-end">{renderMoney(o.dealer_price_snapshot)}</td>
		</tr>
	));

	let body: React.ReactNode;

	if (isLoading) {
		body = (
			<tr>
				<td colSpan={COLUMN_COUNT} className="text-center" style={{ fontSize: '3em' }}>
					<FontAwesomeIcon icon={faSpinner} spin className="green-text" />
				</td>
			</tr>
		);
	} else if (error !== '') {
		body = (
			<tr>
				<td colSpan={COLUMN_COUNT} className="text-center">
					<h5 className="text-danger">{error}</h5>
				</td>
			</tr>
		);
	} else if (orders.length === 0) {
		body = (
			<tr>
				<td colSpan={COLUMN_COUNT} className="text-center">
					<h5 className="green-text">No awning orders found</h5>
				</td>
			</tr>
		);
	} else if (filteredOrders.length === 0) {
		body = (
			<tr>
				<td colSpan={COLUMN_COUNT} className="text-center">
					<h5 className="green-text">No awning orders match the current filters</h5>
				</td>
			</tr>
		);
	} else {
		body = rows;
	}

	return (
		<Container fluid className="local-bootstrap">
			<Card className="mt-2">
				<Card.Body>
					<Row className="align-items-end mb-3">
						<Col lg={4} md={6} sm={12} className="mb-2 mb-md-0">
							<Form.Label className="text-muted" style={{ fontSize: '0.8em' }}>
								Search
							</Form.Label>
							<Form.Control
								type="text"
								placeholder="Quote #, dealer, distributor, territory..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</Col>
						<Col lg={3} md={4} sm={12} className="mb-2 mb-md-0">
							<Form.Label className="text-muted" style={{ fontSize: '0.8em' }}>
								Status
							</Form.Label>
							<Form.Select
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
							>
								<option value="">All Statuses</option>
								{statusOptions.map((s) => (
									<option key={s} value={s}>{formatOrderStatus(s)}</option>
								))}
							</Form.Select>
						</Col>
						<Col lg="auto" md={2} sm={12} className="ms-auto text-end">
							<Button color="green" loading={isLoading} onClick={loadReport}>
								Refresh
							</Button>
						</Col>
					</Row>
					<Row>
						<Col>
							<div className="text-muted mb-2" style={{ fontSize: '0.9em' }}>
								{isLoading || error !== ''
									? null
									: `Showing ${filteredOrders.length} of ${orders.length} awning ${orders.length === 1 ? 'order' : 'orders'}`}
							</div>
							<div className="table-responsive">
								<table className="table table-striped">
									<thead>
										<tr>
											<th>Quote #</th>
											<th>Status</th>
											<th>Dealer</th>
											<th>Distributor</th>
											<th>Territory</th>
											<th className="text-end">Cost</th>
											<th className="text-end">Distributor Price</th>
											<th className="text-end">Dealer Price</th>
										</tr>
									</thead>
									<tbody>
										{body}
									</tbody>
									{!isLoading && error === '' && filteredOrders.length > 0 ? (
										<tfoot>
											<tr className="bold-row">
												<th colSpan={5}>Totals</th>
												<th className="text-end"><Money value={totals.cost} /></th>
												<th className="text-end"><Money value={totals.dist} /></th>
												<th className="text-end"><Money value={totals.dealer} /></th>
											</tr>
										</tfoot>
									) : null}
								</table>
							</div>
						</Col>
					</Row>
				</Card.Body>
			</Card>
		</Container>
	);
};

export default AwningOrdersReport;
